// Date range utilities — Amazon India marketplace calendar (Asia/Kolkata)

const REPORT_TIMEZONE = 'Asia/Kolkata';

export function toReportISODate(date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: REPORT_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/** @deprecated use toReportISODate — kept for callers expecting local name */
export function toLocalISODate(date = new Date()) {
  return toReportISODate(date);
}

export function addReportDays(isoDate, days) {
  const normalized = String(isoDate || '').slice(0, 10);
  const ms = Date.parse(`${normalized}T12:00:00+05:30`) + days * 86400000;
  return toReportISODate(new Date(ms));
}

/** @deprecated use addReportDays */
export function addLocalDays(isoDate, days) {
  return addReportDays(isoDate, days);
}

export function countInclusiveDays(startDate, endDate) {
  const start = String(startDate).slice(0, 10);
  const end = String(endDate).slice(0, 10);
  const startMs = Date.parse(`${start}T12:00:00+05:30`);
  const endMs = Date.parse(`${end}T12:00:00+05:30`);
  return Math.max(1, Math.round((endMs - startMs) / 86400000) + 1);
}

/** First paint for long ranges: fetch this many days (plus server previous-period compare). */
export const DASHBOARD_INITIAL_FETCH_DAYS =
  Number(process.env.REACT_APP_DASHBOARD_INITIAL_MAX_DAYS) || 30;

/**
 * For ranges longer than maxDays, return a shorter window ending on the same end date.
 */
export function getStagedDashboardFetchBounds(startDate, endDate, maxDays = DASHBOARD_INITIAL_FETCH_DAYS) {
  const days = countInclusiveDays(startDate, endDate);
  if (days <= maxDays) {
    return {
      startDate,
      endDate,
      fullStartDate: startDate,
      fullEndDate: endDate,
      staged: false,
      requestedDays: days,
    };
  }
  const fetchStart = addReportDays(endDate, -(maxDays - 1));
  return {
    startDate: fetchStart,
    endDate,
    fullStartDate: startDate,
    fullEndDate: endDate,
    staged: true,
    requestedDays: days,
  };
}

/** Preset list shared by every DateRangeFilter (order matches Amazon Ads). */
export const METRICS_DATE_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'This week', value: 'thisWeek' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Last 30 days', value: '30d' },
  { label: 'Last 60 days', value: '60d' },
  { label: 'Last 90 days', value: '90d' },
  { label: 'This month', value: 'thisMonth' },
];

/** Monday-start calendar week in REPORT_TIMEZONE (inclusive through endDate). */
export function startOfReportWeek(isoDate) {
  const anchor = String(isoDate || toReportISODate()).slice(0, 10);
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: REPORT_TIMEZONE,
    weekday: 'short',
  }).format(new Date(`${anchor}T12:00:00+05:30`));
  const map = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dow = map[weekday] ?? 0;
  const daysSinceMonday = (dow + 6) % 7;
  return addReportDays(anchor, -daysSinceMonday);
}

/** Rolling N-day presets (7/30/60/90): end yesterday — full settled days only. */
function rollingPresetBounds(dayCount) {
  const yesterday = addReportDays(toReportISODate(), -1);
  return {
    startDate: addReportDays(yesterday, -(dayCount - 1)),
    endDate: yesterday,
    timezone: REPORT_TIMEZONE,
  };
}

export function iterateReportDates(startDate, endDate) {
  const start = String(startDate).slice(0, 10);
  const end = String(endDate).slice(0, 10);
  if (!start || !end || start > end) return [];
  const dates = [];
  let cursor = start;
  while (cursor <= end) {
    dates.push(cursor);
    cursor = addReportDays(cursor, 1);
  }
  return dates;
}

export function formatChartAxisDate(isoDate) {
  const normalized = String(isoDate).slice(0, 10);
  const ms = Date.parse(`${normalized}T12:00:00+05:30`);
  return new Date(ms).toLocaleDateString('en-US', {
    timeZone: REPORT_TIMEZONE,
    month: 'short',
    day: 'numeric',
  });
}

export function resolveDateRangeBounds(dateRange, customDateRange) {
  if (customDateRange?.startDate && customDateRange?.endDate) {
    const startDate = customDateRange.startDate <= customDateRange.endDate
      ? customDateRange.startDate
      : customDateRange.endDate;
    const endDate = customDateRange.startDate <= customDateRange.endDate
      ? customDateRange.endDate
      : customDateRange.startDate;
    return { startDate, endDate, timezone: REPORT_TIMEZONE };
  }

  const today = toReportISODate();

  switch (dateRange) {
    case 'today':
    case '1d':
      return { startDate: today, endDate: today, timezone: REPORT_TIMEZONE };
    case 'yesterday': {
      const y = addReportDays(today, -1);
      return { startDate: y, endDate: y, timezone: REPORT_TIMEZONE };
    }
    case 'thisWeek':
      return {
        startDate: startOfReportWeek(today),
        endDate: today,
        timezone: REPORT_TIMEZONE,
      };
    case '7d':
      return rollingPresetBounds(7);
    case '30d':
      return rollingPresetBounds(30);
    case '60d':
      return rollingPresetBounds(60);
    case '90d':
      return rollingPresetBounds(90);
    case 'thisMonth': {
      const [year, month] = today.split('-');
      return {
        startDate: `${year}-${month}-01`,
        endDate: today,
        timezone: REPORT_TIMEZONE,
      };
    }
    default:
      return rollingPresetBounds(30);
  }
}

export function previousPeriodBounds(startDate, endDate, dateRange) {
  if (dateRange === 'thisMonth') {
    const lastMonthEnd = addReportDays(startDate, -1);
    const [year, month] = lastMonthEnd.split('-').map(Number);
    const lastMonthStart = `${year}-${String(month).padStart(2, '0')}-01`;
    return { startDate: lastMonthStart, endDate: lastMonthEnd };
  }
  if (dateRange === 'thisWeek') {
    const previousEnd = addReportDays(startDate, -1);
    const previousStart = addReportDays(previousEnd, -6);
    return { startDate: previousStart, endDate: previousEnd };
  }
  const rangeDays = countInclusiveDays(startDate, endDate);
  const previousEnd = addReportDays(startDate, -1);
  const previousStart = addReportDays(previousEnd, -(rangeDays - 1));
  return { startDate: previousStart, endDate: previousEnd };
}

export function normalizeReportDate(dateValue) {
  const raw = String(dateValue || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  return '';
}

export function isDateInRange(dateValue, startDate, endDate) {
  const day = normalizeReportDate(dateValue);
  if (!day) return false;
  return day >= startDate && day <= endDate;
}

export const getComparisonText = (dateRange) => {
  if (typeof dateRange === 'object' && dateRange.type === 'custom') {
    return 'vs previous period';
  }

  switch (dateRange) {
    case 'today':
      return 'vs yesterday';
    case 'yesterday':
      return 'vs day before yesterday';
    case 'thisWeek':
      return 'vs previous week';
    case '7d':
      return 'vs previous 7 days';
    case '30d':
      return 'vs previous 30 days';
    case '60d':
      return 'vs previous 60 days';
    case '90d':
      return 'vs previous 90 days';
    case 'thisMonth':
      return 'vs last month';
    default:
      return 'vs previous period';
  }
};

export const getPeriodLabel = (dateRange) => {
  if (typeof dateRange === 'object' && dateRange.type === 'custom') {
    return 'Custom Period';
  }

  switch (dateRange) {
    case 'today':
      return 'Today';
    case 'yesterday':
      return 'Yesterday';
    case 'thisWeek':
      return 'This week';
    case '7d':
      return 'Last 7 days';
    case '30d':
      return 'Last 30 days';
    case '60d':
      return 'Last 60 days';
    case '90d':
      return 'Last 90 days';
    case 'thisMonth':
      return 'This month';
    default:
      return 'Last 30 days';
  }
};

export { REPORT_TIMEZONE };
